import React, { forwardRef } from 'react';
import { Search } from 'lucide-react';
import '../DesignTab.css';
import './CompareSegmentPicker.css';
import { segmentColor } from './CompareChart';

// The Segments an author defined in Design, presented the same way, for
// selecting rather than editing. Columns match the Design Segments table —
// name, mappings, total recipients — so the same list reads the same in both
// places.
const CompareSegmentPicker = forwardRef(function CompareSegmentPicker({
  candidates = [],
  selectedKeys,
  keyOf,
  onToggle,
  onClearSelection,
  onClearSearch,
  query,
  onQueryChange,
  loading = false,
  error = '',
  page = 1,
  pageCount = 1,
  onPageChange,
  emptyMessage = 'No Segments are defined. Define Segments in Design.',
  colorIndexOf,
  // Why this candidate is inconsistent with the declared intent, or '' when it
  // is selectable (Section 11.1.1). A disabled row keeps its name, survey, and
  // count: the author has to be able to see what they cannot pick, and why.
  disabledReasonOf = () => '',
}, searchRef) {
  const rows = () => {
    if (loading) return <tr><td colSpan="4" className="design-empty">Loading Segment mappings…</td></tr>;
    if (error) return <tr><td colSpan="4" className="design-empty design-backend-error" role="alert">{error}</td></tr>;
    if (!candidates.length) {
      return <tr><td colSpan="4" className="design-empty">{query ? 'No Segment mappings match your search.' : emptyMessage}</td></tr>;
    }
    return candidates.map(candidate => {
      const key = keyOf(candidate);
      const checked = selectedKeys.has(key);
      const colorIndex = colorIndexOf(key);
      const name = candidate.logical_segment_name || 'Segment';
      const disabledReason = disabledReasonOf(candidate);
      return (
        <tr
          key={key}
          className={`${checked ? 'design-selected-row' : ''}${disabledReason ? ' compare-segment-unavailable' : ''}`.trim()}
          title={disabledReason || undefined}
        >
          <td>
            <input
              type="checkbox"
              checked={checked}
              disabled={Boolean(disabledReason)}
              onChange={() => onToggle(candidate)}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); onToggle(candidate); } }}
              aria-label={disabledReason ? `${name} in ${candidate.deployment_label || 'deployment'} is unavailable: ${disabledReason}` : `Select ${name} in ${candidate.deployment_label || 'deployment'}`}
            />
          </td>
          <td>
            <span className="compare-segment-name">
              {checked && colorIndex >= 0 && (
                <span
                  className="compare-segment-swatch"
                  style={{ backgroundColor: segmentColor(colorIndex) }}
                  aria-hidden="true"
                />
              )}
              <strong>{name}</strong>
            </span>
          </td>
          <td>{candidate.survey_label || 'Survey unavailable'}</td>
          <td>{candidate.included_recipient_count ?? 0}</td>
        </tr>
      );
    });
  };

  return (
    <section className="design-list-card compare-segment-picker" aria-labelledby="compare-segments-heading">
      <div className="design-list-heading"><div><h3 id="compare-segments-heading">Segments</h3></div></div>
      <label className="design-search" htmlFor="analysis-segment-search">
        <Search size={14} />
        <span className="sr-only">Search Segment mappings</span>
        <input
          ref={searchRef}
          id="analysis-segment-search"
          value={query}
          onChange={event => onQueryChange(event.target.value)}
          placeholder="Segment, survey, or deployment"
        />
      </label>
      <div className="design-table-scroll">
        <table>
          <thead>
            <tr>
              <th>Compare</th>
              <th>Segment name</th>
              <th>Survey</th>
              <th>Recipients</th>
            </tr>
          </thead>
          <tbody>{rows()}</tbody>
        </table>
      </div>
      <div className="design-selection-row">
        <span>{selectedKeys.size} selected</span>
        <button type="button" onClick={onClearSearch}>Clear search</button>
        <button type="button" onClick={onClearSelection}>Clear selection</button>
      </div>
      <div className="design-pagination" aria-label="Segment mapping pages">
        <span>Page {page} of {pageCount}</span>
        <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Previous page</button>
        <button type="button" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)}>Next page</button>
      </div>
    </section>
  );
});

export default CompareSegmentPicker;
